import { useContext, useState } from 'react';
import {
  Badge,
  EntityText,
  PersonSummary,
  ReplyComposer,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { PEOPLE, REPLY_TEMPLATES, replyVariables } from './personModel';
import { useNavigate } from 'react-router-dom';
import type { MessageActions } from '@/lib/conversation';
import { HOME } from './model';
import { PersonTimeZoneContext } from '@/lib/personTimeZone';

export function PeopleDemo({ onEntityOpen }: Pick<MessageActions, 'onEntityOpen'>) {
  const [selected, setSelected] = useState(PEOPLE[0].id);
  const person = PEOPLE.find((person) => person.id === selected) ?? PEOPLE[0];
  const navigate = useNavigate();
  const display = useContext(PersonTimeZoneContext);
  return (
    <section data-slot="people-demo" aria-label="Человек и учётки" className="min-w-0 space-y-4">
      <header>
        <h3 className="type-heading">Один человек — несколько каналов</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Связанные учётки, пояс и дежурство доступны прямо у имени.
        </p>
      </header>
      <div className="flex max-w-2xl flex-wrap items-center gap-3">
        <div className="w-80 max-w-full">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger aria-label="Участник образца">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PEOPLE.map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {display.onModeChange && (
          <div className="w-56 max-w-full">
            <Select
              value={display.mode}
              onValueChange={(value) => display.onModeChange?.(value as 'moscow' | 'local')}
            >
              <SelectTrigger aria-label="Пояс времени данных">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="moscow">Время данных · МСК</SelectItem>
                <SelectItem value="local">Время данных · местное</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="space-y-4 rounded-lg border border-line bg-card p-4">
          <Badge>Имя в тексте</Badge>
          <p className="text-sm leading-5 text-fg">
            Сообщение от{' '}
            <EntityText
              name={person.name}
              person={person}
              onClick={(event) =>
                onEntityOpen
                  ? onEntityOpen({ ...person, kind: 'user' }, event.currentTarget)
                  : navigate(HOME + '/entity/' + person.id)
              }
            />
            , можно продолжить разговор в другом канале.
          </p>
          <p className="text-xs text-fg-muted">
            Наведи на имя или перейди к нему клавишей Tab. Нажатие открывает свойства.
          </p>
        </div>
        <div className="rounded-lg border border-line bg-card p-4">
          <PersonSummary person={person} />
        </div>
      </div>
    </section>
  );
}
export function TemplatesDemo() {
  const [value, setValue] = useState('');
  const [variables, setVariables] = useState(replyVariables);
  const [sent, setSent] = useState('');
  return (
    <section data-slot="templates-demo" aria-label="Шаблоны ответов" className="min-w-0 space-y-4">
      <header>
        <h3 className="type-heading">Шаблоны ответов</h3>
        <p className="mt-1 text-sm text-fg-muted">
          Выбери заготовку, заполни поля и проверь ответ. Все данные вымышлены.
        </p>
      </header>
      <ReplyComposer
        value={value}
        onValueChange={setValue}
        templates={REPLY_TEMPLATES}
        variables={variables}
        onVariableChange={(key, next) =>
          setVariables((current) =>
            current.map((item) => (item.key === key ? { ...item, value: next } : item)),
          )
        }
        onSubmit={(text) => {
          setSent(text);
          setValue('');
        }}
        submitLabel="Сохранить ответ в образце"
      />
      <p role="status" className="text-sm text-success-fg">
        {sent ? 'Ответ сохранён только в образце.' : ''}
      </p>
      {sent && (
        <div data-slot="saved-template-reply" className="rounded-md border border-line bg-card p-4">
          <p className="mb-2 type-overline text-fg-muted">Сохранённый ответ</p>
          <p className="text-sm leading-5 break-words whitespace-pre-wrap">{sent}</p>
        </div>
      )}
    </section>
  );
}
